import { Box, Chip, FormLabel, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AccountCircle, CorporateFare, Logout, Menu, Person } from '@mui/icons-material';
import LanguageSwitcherIcon from '@/components/i18n/LanguageSwitcherIcon';
import BaseButton from '@/components/ui/BaseButton';
import PopoverMenu from '@/components/ui/PopoverMenu';
import { FC, useCallback, useState } from 'react';
import { useAppContext } from '@/provider/contexts';
import { performLogout } from '@/services/logoutService';
import { SyncSessionService } from '@/services/syncSessionService';
import { decodeToken } from '@/utils/tokenUtils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import localForage from 'localforage';
import BaseDialog from '@/components/ui/BaseDialog';

type AccountInfoChipsProps = {
    username?: string;
    corporateName?: string;
    hasMultiCorporation: boolean;
    onCorporationChange: () => void;
};

type HeaderProps = {
    toggleSidebar?: () => void;
};

const HeaderRoot = styled('header')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
    height: 50,
    padding: '0 30px',
    backgroundColor: '#f2f2f2',
    color: '#595959',
    boxSizing: 'border-box',

    [theme.breakpoints.down('md')]: {
        justifyContent: 'space-between',
        padding: '0 16px',
    },
}));

const SidebarToggler = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',

    [theme.breakpoints.up('md')]: {
        display: 'none',
    },
}));

const MobileUserSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',

    [theme.breakpoints.up('md')]: {
        display: 'none',
    },
}));

const DesktopUserSection = styled(Box)(({ theme }) => ({
    display: 'none',
    alignItems: 'center',
    minWidth: 0,

    [theme.breakpoints.up('md')]: {
        display: 'flex',
    },
}));

const PopoverContent = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '0 8px 8px',
});

const StyledAccountInfoChips = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));

const HeaderIconButton = styled(IconButton)({
    width: 36,
    height: 36,
    borderRadius: 8,
    margin: '0 4px',
});

const AccountInfoChips: FC<AccountInfoChipsProps> = ({
    username,
    corporateName,
    hasMultiCorporation,
    onCorporationChange,
}) => {
    return (
        <StyledAccountInfoChips>
            <Chip icon={<Person />} color="secondary" label={username || ''} />

            {corporateName && (
                <Chip
                    icon={<CorporateFare />}
                    color="secondary"
                    variant="outlined"
                    label={corporateName}
                    clickable={hasMultiCorporation}
                    onClick={hasMultiCorporation ? onCorporationChange : undefined}
                />
            )}
        </StyledAccountInfoChips>
    );
};

const AppHeader: FC<HeaderProps> = ({ toggleSidebar }) => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const {
        setManageStoreId,
        userDetails,
        setUserDetails,
        corporationDetails,
        setCorporationDetails,
        setNotification,
    } = useAppContext();
    // State
    const [loading, setLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const hasMultiCorporation = localStorage.getItem('hasMultiCorporation') === 'true';

    // Handlers
    const logoutHandler = async () => {
        setLoading(true);
        try {
            const rawCorp = await localForage.getItem<string>('corporate_token');
            const decoded = rawCorp ? decodeToken(rawCorp) : undefined;
            await performLogout(decoded ? [decoded] : []);

            clearUserAndCorporationDetails();
            setManageStoreId(null);

            setNotification({ severity: 'success', message: t('Logout successful!') });
            SyncSessionService.postMessage({
                type: 'logout',
            });
            navigate('/login', { replace: true });
        } catch {
            setNotification({ severity: 'error', message: t('Logout failed. Please try again.') });
        } finally {
            setLoading(false);
        }
    };

    const handleCorporationChange = useCallback(() => {
        if (!hasMultiCorporation) return;
        setIsDialogOpen(true);
    }, [hasMultiCorporation]);

    const handleDialogClose = useCallback(() => {
        setIsDialogOpen(false);
    }, []);

    const confirmCorporationChange = useCallback(() => {
        const emptyCorporationDetails = {
            ...corporationDetails,
            token: '',
            username: 'Guest',
            corporate_name: '',
            user_level: 0,
            user_abilities: [],
            token_expires: null,
        };
        setCorporationDetails(emptyCorporationDetails);
        localStorage.removeItem('corporationDetails');
        SyncSessionService.postMessage({
            type: 'change',
            data: emptyCorporationDetails,
        });
        navigate('/corporation-selection', { replace: true });
    }, [corporationDetails]);

    // Helper functions
    const clearUserAndCorporationDetails = () => {
        setUserDetails({
            token: '',
            username: 'Guest',
            user_level: 0,
            user_abilities: [],
            token_expires: null,
        });
        setCorporationDetails({
            token: '',
            username: 'Guest',
            corporate_name: '',
            user_level: 0,
            user_abilities: [],
            token_expires: null,
        });
    };

    return (
        <>
            <HeaderRoot>
                <SidebarToggler>
                    <HeaderIconButton onClick={toggleSidebar} aria-label={t('Toggle sidebar')}>
                        <Menu />
                    </HeaderIconButton>
                </SidebarToggler>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        minWidth: 0,
                    }}
                >
                    <MobileUserSection>
                        <PopoverMenu title="User Info" icon={<AccountCircle />} isIconButton>
                            <PopoverContent>
                                <FormLabel>{t('Account Info')}</FormLabel>

                                <AccountInfoChips
                                    username={userDetails?.username}
                                    corporateName={corporationDetails?.corporate_name}
                                    hasMultiCorporation={hasMultiCorporation}
                                    onCorporationChange={handleCorporationChange}
                                />
                            </PopoverContent>
                        </PopoverMenu>
                    </MobileUserSection>

                    <LanguageSwitcherIcon />

                    <DesktopUserSection>
                        <AccountInfoChips
                            username={userDetails?.username}
                            corporateName={corporationDetails?.corporate_name}
                            hasMultiCorporation={hasMultiCorporation}
                            onCorporationChange={handleCorporationChange}
                        />
                    </DesktopUserSection>

                    <BaseButton
                        variant="contained"
                        color="error"
                        size="small"
                        loading={loading}
                        onClick={logoutHandler}
                        startIcon={<Logout />}
                    >
                        {t('Logout')}
                    </BaseButton>
                </Box>
            </HeaderRoot>
            <BaseDialog
                open={isDialogOpen}
                dialogTitle={t('Change Corporation')}
                confirmButtonText={t('YES')}
                cancelButtonText={t('NO')}
                onConfirm={confirmCorporationChange}
                onClose={handleDialogClose}
            >
                <Typography>{t('Are you sure you want to change corporation?')}</Typography>
            </BaseDialog>
        </>
    );
};

export default AppHeader;
